import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import PlayIcon from "@mui/icons-material/PlayArrowRounded";
import EditIcon from "@mui/icons-material/EditOutlined";
import HistoryIcon from "@mui/icons-material/HistoryOutlined";
import PauseIcon from "@mui/icons-material/PauseRounded";
import CalendarIcon from "@mui/icons-material/EventRepeatOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMoveOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopyOutlined";
import ArchiveIcon from "@mui/icons-material/Inventory2Outlined";
import { BackfillDialog } from "@/components/pipeline-canvas/backfill-dialog";
import { EditDetailsDialog } from "@/components/common/edit-details-dialog";
import { EntityActionsMenu } from "@/components/common/entity-actions-menu";
import { MoveToWorkspaceDialog } from "@/components/common/move-to-workspace-dialog";
import { PageContainer, PageHeader } from "@/components/layout/page";
import { Cluster } from "@/components/layout/cluster";
import { Breadcrumbs } from "@/components/nav/breadcrumbs";
import { StatusChip } from "@/components/display/status-chip";
import { RelativeTime } from "@/components/display/relative-time";
import { IdChip } from "@/components/display/mono";
import { LoadingState, ErrorState } from "@/components/feedback/states";
import { useToast } from "@/components/feedback/toaster";
import { pipelineQuery, usePipeline, usePipelineMutations } from "@/api/pipelines";
import { useConnectorNames } from "@/api/connectors";
import { useLiveRuns } from "@/stores/live-runs";
import { useUndoableDelete } from "@/lib/undoable-delete";

export const Route = createFileRoute("/_app/pipelines/$pipelineId/")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(pipelineQuery(params.pipelineId)),
  component: PipelineOverview,
});

function PipelineOverview() {
  const { pipelineId } = Route.useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { data: p, isLoading, error, refetch } = usePipeline(pipelineId);
  const names = useConnectorNames();
  const m = usePipelineMutations(pipelineId);
  const trackRun = useLiveRuns((s) => s.track);
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const undoableDelete = useUndoableDelete<{ id: string; name: string }>({
    noun: "Pipeline",
    key: (i) => i.id,
    name: (i) => i.name,
    mutate: (i) => m.remove.mutateAsync(i.id),
    invalidate: [["pipelines"]],
  });

  if (isLoading) return <LoadingState />;
  if (error || !p) return <ErrorState error={error ?? "Not found"} onRetry={() => refetch()} />;

  const isActive = p.status === "ACTIVE";

  function onDelete() {
    undoableDelete.remove(
      { id: pipelineId, name: p!.name },
      { onUndo: () => navigate({ to: "/pipelines/$pipelineId", params: { pipelineId } }) },
    );
    navigate({ to: "/pipelines" });
  }

  return (
    <PageContainer maxWidth={860}>
      <PageHeader
        title={p.name}
        breadcrumbs={
          <Breadcrumbs items={[{ label: "Pipelines", to: "/pipelines" }, { label: p.name }]} />
        }
        description={p.description ?? undefined}
        actions={
          <>
            <Button
              variant="contained"
              startIcon={<PlayIcon />}
              onClick={async () => {
                const res = await m.run.mutateAsync(pipelineId);
                if (res?.executionId) {
                  trackRun({ executionId: res.executionId, pipelineId, label: p!.name });
                }
                toast.success("Run started — you'll get a toast when it finishes");
              }}
            >
              Run
            </Button>
            <Button
              startIcon={isActive ? <PauseIcon /> : <PlayIcon />}
              onClick={async () => {
                await m.setActive.mutateAsync({ pid: pipelineId, active: !isActive });
                toast.success(isActive ? "Paused" : "Activated");
              }}
            >
              {isActive ? "Pause" : "Activate"}
            </Button>
            <Button
              startIcon={<EditIcon />}
              onClick={() =>
                navigate({ to: "/pipelines/$pipelineId/edit", params: { pipelineId } })
              }
            >
              Edit
            </Button>
            <Button
              startIcon={<HistoryIcon />}
              onClick={() =>
                navigate({ to: "/pipelines/$pipelineId/runs", params: { pipelineId } })
              }
            >
              Runs
            </Button>
            <Button startIcon={<CalendarIcon />} onClick={() => setBackfillOpen(true)}>
              Backfill
            </Button>
            <EntityActionsMenu
              items={[
                {
                  label: "Edit details",
                  icon: <DriveFileRenameOutlineIcon fontSize="small" />,
                  onClick: () => setEditOpen(true),
                },
                {
                  label: "Move to workspace…",
                  icon: <DriveFileMoveIcon fontSize="small" />,
                  onClick: () => setMoveOpen(true),
                },
                {
                  label: "Duplicate",
                  icon: <ContentCopyIcon fontSize="small" />,
                  onClick: async () => {
                    const dup = await m.duplicate.mutateAsync(pipelineId);
                    toast.success("Pipeline duplicated");
                    navigate({
                      to: "/pipelines/$pipelineId",
                      params: { pipelineId: dup.id },
                    });
                  },
                },
                {
                  label: p.archivedAt ? "Unarchive" : "Archive",
                  icon: <ArchiveIcon fontSize="small" />,
                  divider: true,
                  onClick: async () => {
                    await m.setArchived.mutateAsync({
                      pid: pipelineId,
                      archived: !p.archivedAt,
                    });
                    toast.success(p.archivedAt ? "Unarchived" : "Archived");
                  },
                },
                {
                  label: "Delete",
                  icon: <DeleteIcon fontSize="small" />,
                  onClick: onDelete,
                  danger: true,
                },
              ]}
            />
          </>
        }
      />

      <BackfillDialog
        open={backfillOpen}
        onClose={() => setBackfillOpen(false)}
        pipelineId={pipelineId}
      />

      <EditDetailsDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        noun="pipeline"
        initial={{ name: p.name, description: p.description ?? "" }}
        onSave={(v) => m.update.mutateAsync({ name: v.name, description: v.description })}
      />

      <MoveToWorkspaceDialog
        open={moveOpen}
        onClose={() => setMoveOpen(false)}
        noun="pipeline"
        currentWorkspaceId={p.workspaceId}
        onMove={(workspaceId) => m.moveWorkspace.mutateAsync({ pid: pipelineId, workspaceId })}
      />

      <Card sx={{ p: 2.5, mb: 3 }}>
        <Cluster gap={4} align="start">
          <Field label="Status">
            <StatusChip status={p.status} />
            {p.archivedAt ? <Chip size="small" label="Archived" sx={{ ml: 1 }} /> : null}
          </Field>
          <Field label="Mode">{p.executionMode ?? "BATCH"}</Field>
          <Field label="Source">{names[p.sourceConnectorId ?? ""] ?? "—"}</Field>
          <Field label="Destination">{names[p.destConnectorId ?? ""] ?? "—"}</Field>
          <Field label="Updated">
            <RelativeTime value={p.updatedAt} />
          </Field>
          <Field label="ID">
            <IdChip value={p.id} truncate={12} />
          </Field>
        </Cluster>
      </Card>

      <Typography variant="h3" sx={{ mb: 1.5 }}>
        Steps
      </Typography>
      <Card sx={{ p: 2 }}>
        {(p.steps ?? []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No transform steps — data passes straight through. Use <strong>Edit</strong> to add
            some.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {[...(p.steps ?? [])]
              .sort((a, b) => a.stepOrder - b.stepOrder)
              .map((s, i) => (
                <Stack key={s.id ?? i} direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                  <Chip size="small" label={i + 1} />
                  <Chip size="small" variant="outlined" label={s.stepType} />
                  <Typography variant="body2">{s.name ?? s.stepType}</Typography>
                  {s.enabled === false ? (
                    <Chip size="small" color="default" label="disabled" />
                  ) : null}
                </Stack>
              ))}
          </Stack>
        )}
      </Card>
    </PageContainer>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
        {label}
      </Typography>
      <Box sx={{ fontSize: "0.875rem" }}>{children}</Box>
    </Box>
  );
}
